import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People,
  ShoppingCart,
  Inventory,
  AttachMoney,
} from '@mui/icons-material';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../services/api';

interface DashboardStats {
  totalUsers: number;
  totalProducts: number;
  totalOrders: number;
  totalRevenue: number;
  monthlyRevenue: number;
  newUsersThisMonth: number;
  pendingOrders: number;
  completedOrders: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => (
  <Card
    sx={{
      height: '100%',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      border: `1px solid ${color}20`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${color}25`,
      },
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: trend ? 0.5 : 0,
            }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {trend && (
            <Chip
              label={trend}
              size="small"
              sx={{
                backgroundColor: `${color}20`,
                color: color,
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}20`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon as React.ReactElement<any>, {
            style: { color: color, fontSize: 28 },
          })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 디버깅: 토큰 확인
        const accessToken = localStorage.getItem('accessToken');
        const userRole = localStorage.getItem('userRole');
        console.log('[AdminDashboard] accessToken:', accessToken ? 'exists' : 'null');
        console.log('[AdminDashboard] userRole:', userRole);

        if (!accessToken) {
          setError('Login required.');
          return;
        }

        if (userRole !== 'ADMIN' && userRole !== 'SUPER_ADMIN') {
          setError('Administrator privileges required.');
          return;
        }

        const response = await api.get('/admin/dashboard');

        // 백엔드 응답을 DashboardStats 인터페이스에 맞게 매핑
        const dashboardData: DashboardStats = {
          totalUsers: response.data.totalUsers || 0,
          totalProducts: response.data.totalProducts || 0,
          totalOrders: response.data.totalOrders || 0,
          totalRevenue: response.data.totalRevenue || 0,
          monthlyRevenue: response.data.monthlyRevenue || 0,
          newUsersThisMonth: response.data.newUsersThisMonth || 0,
          pendingOrders: response.data.pendingOrders || 0,
          completedOrders: response.data.completedOrders || 0,
        };

        setStats(dashboardData);
      } catch (err: any) {
        console.error('Dashboard data fetch error:', err);
        console.error('Error status:', err.response?.status);
        console.error('Error data:', err.response?.data);

        if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Authentication expired. Please log in again.');
          // 토큰 제거
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userRole');
          // 로그인 페이지로 리다이렉트 (필요시)
          // window.location.href = '/login';
        } else {
          setError(
            err.response?.data?.message ||
            err.response?.data?.error ||
            'Failed to load dashboard data.'
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#35C5F0' }} />
        </Box>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Unable to load dashboard data.
        </Alert>
      </AdminLayout>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(amount);
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#333',
            mb: 2,
          }}
        >
          Welcome! 👋
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Check today's business status
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={<People />}
          color="#35C5F0"
          trend={`+${stats.newUsersThisMonth} this month`}
        />
        <StatCard
          title="Total Products"
          value={stats.totalProducts}
          icon={<Inventory />}
          color="#FF7F2F"
        />
        <StatCard
          title="Total Orders"
          value={stats.totalOrders}
          icon={<ShoppingCart />}
          color="#4CAF50"
          trend={`${stats.pendingOrders} pending`}
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          icon={<AttachMoney />}
          color="#9C27B0"
          trend={`${formatCurrency(stats.monthlyRevenue)} this month`}
        />
      </Box>

      {/* Recent Activity */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr'
          },
          gap: 3,
        }}
      >
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Recent Orders
            </Typography>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>#ORD-001</TableCell>
                    <TableCell>John Doe</TableCell>
                    <TableCell>{formatCurrency(125000)}</TableCell>
                    <TableCell>
                      <Chip
                        label="Completed"
                        size="small"
                        sx={{ backgroundColor: '#4CAF5020', color: '#4CAF50' }}
                      />
                    </TableCell>
                    <TableCell>2024-01-15</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>#ORD-002</TableCell>
                    <TableCell>Jane Smith</TableCell>
                    <TableCell>{formatCurrency(87000)}</TableCell>
                    <TableCell>
                      <Chip
                        label="Shipping"
                        size="small"
                        sx={{ backgroundColor: '#FF7F2F20', color: '#FF7F2F' }}
                      />
                    </TableCell>
                    <TableCell>2024-01-15</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>#ORD-003</TableCell>
                    <TableCell>Mike Park</TableCell>
                    <TableCell>{formatCurrency(203000)}</TableCell>
                    <TableCell>
                      <Chip
                        label="Processing"
                        size="small"
                        sx={{ backgroundColor: '#35C5F020', color: '#35C5F0' }}
                      />
                    </TableCell>
                    <TableCell>2024-01-14</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ height: 'fit-content' }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Quick Stats
            </Typography>
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Completed Orders
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                  {stats.completedOrders}
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Pending Orders
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#FF7F2F' }}>
                  {stats.pendingOrders}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  New Users This Month
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#35C5F0' }}>
                  +{stats.newUsersThisMonth}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};

export default AdminDashboard;